// TODO: move this over to scaleui
import React, { useMemo, useEffect, useState, useRef } from 'react';
import { useMount } from 'react-use-v13';
import {
  BaseCheckbox,
  Checkbox,
  Icon,
  DEFAULTS_TABLE,
  TableColumn,
  TableFooter,
  SelectMenuOption,
} from '@scale/scaleui';
import _ from 'lodash';
import { twMerge } from 'tailwind-merge';
import {
  ColumnDef,
  OnChangeFn,
  RowSelectionState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  sortingFns,
  useReactTable,
  Cell as TCell,
  Column as TColumn,
  Row as TRow,
  Table as TTable,
  filterFns as tableFilterFns,
} from '@tanstack/react-table';

interface NestedTableProps {
  data: object[];
  subtableField: string;
  columns: TableColumn[];
  subtableColumns: TableColumn[];
  enableRowSelection?: boolean;
  rowSelectionState?: RowSelectionState;
  onRowSelectionStateChange?: OnChangeFn<RowSelectionState>;
  pageSizeOptions?: SelectMenuOption<number>[];
  defaultPageSize?: number;
  stripedRows?: boolean;
  divideRows?: boolean;
  subtableWidth?: number;
}

type CellFnType = (props: {
  table: TTable<object>;
  row: TRow<object>;
  column: TColumn<object>;
  cell: TCell<object, any>;
  getValue: () => any;
  renderValue: () => any;
}) => unknown;

const defaultCellFn: CellFnType = info => info.getValue() ?? null;

const NestedTable: React.FC<NestedTableProps> = (tableProps: NestedTableProps) => {
  const {
    data,
    subtableField,
    columns,
    enableRowSelection,
    rowSelectionState,
    onRowSelectionStateChange,
    stripedRows = DEFAULTS_TABLE.stripedRows,
    divideRows = DEFAULTS_TABLE.divideRows,
    pageSizeOptions = DEFAULTS_TABLE.pageSizeOptions,
    defaultPageSize = DEFAULTS_TABLE.defaultPageSize,
  } = tableProps;

  useEffect(() => {
    onRowSelectionStateChange?.({});
  }, [data, onRowSelectionStateChange]);

  const mappedColumns = useMemo<ColumnDef<object>[]>(() => {
    const newColumns: ColumnDef<object>[] = [];

    if (enableRowSelection && rowSelectionState) {
      newColumns.push({
        id: 'select',
        header: function CheckboxHeader(_props) {
          const handleSelectAll = () => {
            if (Object.keys(rowSelectionState).length === data.length) {
              onRowSelectionStateChange?.({});
            } else {
              const allSelected = data.reduce(
                (acc, _d) => ({ ...acc, [Object.keys(acc).length]: true }),
                {},
              );
              onRowSelectionStateChange?.(allSelected as RowSelectionState);
            }
          };
          return (
            <div className="flex items-center justify-center w-full">
              <Checkbox
                id="table-head-checkbox-select-all"
                indeterminate={Object.keys(rowSelectionState).length === data.length}
                onChange={handleSelectAll}
                hideLabel
              />
            </div>
          );
        },
        cell: function CheckboxCell({ row }) {
          return (
            <div className="flex items-center justify-center w-full mt-[10px]">
              <BaseCheckbox
                className="cursor-pointer"
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                }}
              />
            </div>
          );
        },
        accessorFn: () => null,
        meta: {
          headerClassName: 'justify-center',
          isCheckboxHeader: true,
        },
        size: 12,
        maxSize: 12,
        minSize: 12,
      });
    }

    columns.forEach(
      ({
        id,
        headerClassName,
        header = '',
        sortType = 'alphanumeric',
        headerTooltip = '',
        cell = defaultCellFn,
        cellClassName,
        accessorFn = _.property(id),
        enableSorting,
      }) => {
        newColumns.push({
          id,
          header,
          accessorFn,
          cell,
          enableSorting,
          sortingFn: sortType === 'alphanumeric' ? sortingFns[sortType] : sortingFns.basic,
          meta: {
            headerTooltip,
            headerClassName,
            cellClassName,
          },
        });
      },
    );

    return newColumns;
  }, [columns, data, enableRowSelection, onRowSelectionStateChange, rowSelectionState]);

  const table = useReactTable({
    data,
    columns: mappedColumns,
    state: {
      ...(enableRowSelection && rowSelectionState ? { rowSelection: rowSelectionState } : {}),
    },
    enableRowSelection,
    ...(enableRowSelection && onRowSelectionStateChange
      ? { onRowSelectionChange: onRowSelectionStateChange }
      : {}),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    filterFns: tableFilterFns as any,
  });

  useMount(() => {
    table.setPageSize(defaultPageSize);
  });

  const [subtableExpanded, setSubtableExpanded] = useState<Record<string, boolean>>({});

  const tableRef = useRef<any>(null);

  return (
    <div
      className={twMerge(
        'text-neutral-700 shadow ring-1 ring-neutral-1000 ring-opacity-5 divide-y divide-neutral-300 sm:rounded-lg bg-neutral-0 w-full',
      )}
    >
      <div className={twMerge('overflow-x-auto w-full sm:rounded-t-lg')}>
        <table
          className={twMerge(
            'min-w-full max-w-full sm:rounded-t-lg',
            divideRows && 'divide-y divide-neutral-200',
          )}
          ref={tableRef}
        >
          {enableRowSelection && (
            <colgroup>
              <col style={{ width: '60px' }} />
            </colgroup>
          )}
          <thead className={twMerge('bg-neutral-50 h-9 sm:rounded-t-lg')}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id} className={twMerge('sm:rounded-t-lg')}>
                {headerGroup.headers.map(header => (
                  <td
                    key={header.id}
                    className={twMerge('px-3 py-2 text-sm font-medium text-left text-neutral-700')}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(header.column.columnDef.header ?? '', header.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </thead>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                className={twMerge(
                  stripedRows && 'odd:bg-neutral-50',
                  'hover:bg-neutral-100 cursor-default align-top',
                  subtableExpanded[row.id] && 'h-[308px]',
                )}
              >
                {row.getVisibleCells().map((cell, i) => (
                  <>
                    <td
                      key={cell.id}
                      className={twMerge(
                        'pl-3 pr-3 py-2 text-sm font-normal text-neutral-700 whitespace-nowrap relative',
                        !enableRowSelection && i === 0 && 'pl-8',
                      )}
                    >
                      {i === (enableRowSelection ? 1 : 0) && (
                        <div
                          className={twMerge(
                            'cursor-pointer absolute top-[14px]',
                            enableRowSelection ? 'left-0 ml-[-16px]' : 'left-1',
                          )}
                        >
                          {!subtableExpanded[row.id] ? (
                            <Icon
                              size="sm"
                              icon="chevron-down"
                              onClick={() =>
                                setSubtableExpanded(t => ({
                                  ...t,
                                  [row.id]: !(t[row.id] ?? false),
                                }))
                              }
                            />
                          ) : (
                            <Icon
                              size="sm"
                              icon="chevron-up"
                              onClick={() =>
                                setSubtableExpanded(t => ({
                                  ...t,
                                  [row.id]: !(t[row.id] ?? false),
                                }))
                              }
                            />
                          )}
                        </div>
                      )}
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      {i === (enableRowSelection ? 1 : 0) && subtableExpanded[row.id] && (
                        <Icon
                          size="sm"
                          icon={['fas', 'arrow-turn-down-right']}
                          className={twMerge(
                            'absolute top-[52px]',
                            enableRowSelection ? 'left-0 ml-[-16px]' : 'left-1',
                          )}
                        />
                      )}
                      {i === (enableRowSelection ? 1 : 0) && subtableExpanded[row.id] && (
                        <Subtable
                          {...tableProps}
                          data={(data[Number(row.id)] as any)[subtableField]}
                          subtableWidth={tableRef.current?.offsetWidth}
                        />
                      )}
                    </td>
                  </>
                ))}
              </tr>
            ))
          ) : (
            <tr
              className={twMerge(
                stripedRows && 'odd:bg-neutral-50',
                'hover:bg-neutral-100 cursor-default',
              )}
            >
              <td
                colSpan={table.getHeaderGroups()[0].headers.length}
                className={twMerge(
                  'px-3 py-2 text-sm font-normal text-neutral-700 whitespace-nowrap text-center',
                )}
              >
                {'No rows to show'}
              </td>
            </tr>
          )}
        </table>
      </div>
      <TableFooter table={table} pageSizeOptions={pageSizeOptions} />
    </div>
  );
};

const Subtable: React.FC<NestedTableProps> = (tableProps: NestedTableProps) => {
  const {
    data,
    subtableColumns,
    subtableWidth,
    stripedRows = DEFAULTS_TABLE.stripedRows,
    divideRows = DEFAULTS_TABLE.divideRows,
  } = tableProps;

  const mappedSubtableColumns = useMemo<ColumnDef<object>[]>(() => {
    const newColumns: ColumnDef<object>[] = [];
    subtableColumns.forEach(
      ({
        id,
        headerClassName,
        header = '',
        sortType = 'alphanumeric',
        headerTooltip = '',
        cell = defaultCellFn,
        cellClassName,
        accessorFn = _.property(id),
        enableSorting,
      }) => {
        newColumns.push({
          id,
          header,
          accessorFn,
          cell,
          enableSorting,
          sortingFn: sortType === 'alphanumeric' ? sortingFns[sortType] : sortingFns.basic,
          meta: {
            headerTooltip,
            headerClassName,
            cellClassName,
          },
        });
      },
    );

    return newColumns;
  }, [subtableColumns]);

  const subtable = useReactTable({
    data,
    columns: mappedSubtableColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    filterFns: tableFilterFns as any,
  });

  useMount(() => {
    subtable.setPageSize(1000);
  });

  return (
    <div
      className={
        'text-neutral-700 shadow ring-1 ring-neutral-1000 ring-opacity-5 divide-y divide-neutral-300 sm:rounded-lg bg-neutral-0 w-full h-[250px] absolute mt-1 overflow-auto z-[2]'
      }
      style={{ width: (subtableWidth ?? 800) - 80 }}
    >
      <div className={twMerge('overflow-x-auto w-full sm:rounded-t-lg')}>
        <table
          className={twMerge(
            'min-w-full max-w-full sm:rounded-t-lg',
            divideRows && 'divide-y divide-neutral-200',
          )}
        >
          <thead className={twMerge('bg-neutral-50 h-9 sm:rounded-t-lg')}>
            {subtable.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id} className={twMerge('sm:rounded-t-lg')}>
                {headerGroup.headers.map(header => (
                  <td
                    key={header.id}
                    className={twMerge('px-3 py-2 text-sm font-medium text-left text-neutral-700')}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(header.column.columnDef.header ?? '', header.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </thead>
          {subtable.getRowModel().rows.length ? (
            subtable.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                className={twMerge(
                  stripedRows && 'odd:bg-neutral-50',
                  'hover:bg-neutral-100 cursor-default',
                )}
              >
                {row.getVisibleCells().map(cell => (
                  <>
                    <td
                      key={cell.id}
                      className={twMerge(
                        'px-3 py-2 text-sm font-normal text-neutral-700 whitespace-nowrap relative',
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  </>
                ))}
              </tr>
            ))
          ) : (
            <tr
              className={twMerge(
                stripedRows && 'odd:bg-neutral-50',
                'hover:bg-neutral-100 cursor-default h-[220px]',
              )}
            >
              <td
                colSpan={subtable.getHeaderGroups()[0].headers.length}
                className={twMerge(
                  'px-3 py-2 text-sm font-normal text-neutral-700 whitespace-nowrap text-center',
                )}
              >
                {'No rows to show'}
              </td>
            </tr>
          )}
        </table>
      </div>
    </div>
  );
};

export default NestedTable;
